import {
    createAnimation,
    IonButton,
    IonContent,
    IonFooter,
    IonItem,
    IonPage,
    IonText,
    useIonRouter,
} from "@ionic/react";
import { FC, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { HeaderComponent } from "../../components";
import { OnBoardingHeadingComponent } from "./onBoardingHeadingComponent";
import { routes } from "../../route/constants";
import { useDispatch } from "react-redux";
import { setShowOnBoardingFlow } from "../../redux/actions/appStatus";
import { useProfile } from "../../components/hooks";
import { MatchHeader } from "../addResult/matchHeader";
import { MatchPhone, OfficialRole } from "refsix-js-models";
import moment from "moment";
import assignrSquareLogo from "../../assets/images/icon/assignr-square-icon.png";
import refinsightSquareLogo from "../../assets/images/icon/refinsight-square-icon.png";

export const DanishOnBoardingSetupComplete: FC = () => {
    const { t } = useTranslation();
    const route = useIonRouter();
    const dispatch = useDispatch();

    return (
        <>
            <IonContent>
                <OnBoardingHeadingComponent
                    progressValue={1}
                    upperTitleInBold={t(
                        "integrations.dbuIntegrationPopup.title"
                    )}
                    secondTitle={t(
                        "integrations.dbuIntegrationPopup.descriptionMessage"
                    )}
                />
                <MatchHeader
                    match={
                        {
                            date: moment().toISOString(),
                            homeTeam: t("general.home"),
                            awayTeam: t("general.away"),
                            competition: t("fixture.FixtureNew.competition"),
                            venue: t("fixture.FixtureNew.venue"),
                            officialRole: OfficialRole.referee,
                        } as MatchPhone
                    }
                />
            </IonContent>
            <IonFooter>
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        marginTop: 20,
                    }}
                >
                    <IonButton
                        data-testid="button-go-maybe-later"
                        style={{ color: "white" }}
                        fill="clear"
                        onClick={() => {
                            dispatch(setShowOnBoardingFlow(false));
                            route.push(
                                routes.refsixFixtures,
                                "root",
                                "replace"
                            );
                        }}
                    >
                        {t("general.maybeLater")}
                    </IonButton>
                    <IonButton
                        data-testid="button-go-to-dbu-integration"
                        onClick={() => {
                            dispatch(setShowOnBoardingFlow(false));
                            route.push(
                                routes.refsixDbuIntegration,
                                "root",
                                "replace"
                            );
                        }}
                    >
                        {t("general.yes")}
                    </IonButton>
                </div>
            </IonFooter>
        </>
    );
};
export const UsOnBoardingSetupComplete: FC = () => {
    const { t } = useTranslation();
    const route = useIonRouter();
    const dispatch = useDispatch();

    /**
     * Hide the pages during the transition
     */
    const fadeInAnimation = (baseEl: HTMLElement) => {
        return createAnimation()
            .addElement(baseEl)
            .duration(600) // Total duration
            .beforeStyles({ opacity: "0" }) // Ensure it starts invisible
            .afterStyles({ opacity: "1" }) // Ensure it remains visible at the end
            .keyframes([
                { offset: 0, opacity: "0" }, // Stay black initially
                { offset: 0.8, opacity: "0" }, // Hold black for ~100ms (20% of 600ms)
                { offset: 1, opacity: "1" }, // Fade in from 100ms to 600ms
            ]);
    };

    /**
     * Set up the history to the destination page so back button goes through a
     * series of pages from the destination page.
     */
    const goToVia = (
        history: [string, ...string[]],
        destinationPage: string
    ) => {
        const delay = 100;
        route.push(history[0], "root", "replace", undefined, fadeInAnimation);

        const pushPagesSequentially = async () => {
            for (const page of history.slice(1)) {
                await new Promise((resolve) => setTimeout(resolve, delay));
                console.log("pushing page", page);
                route.push(page, "forward", "push", undefined, fadeInAnimation);
            }

            await new Promise((resolve) => setTimeout(resolve, delay));
            route.push(destinationPage, "forward", undefined);
        };

        pushPagesSequentially();
    };

    return (
        <>
            <IonContent>
                <OnBoardingHeadingComponent
                    progressValue={1}
                    upperTitleInBold={t("help.connectIntegrations.title")}
                    secondTitle={t("help.connectIntegrations.secondTitle")}
                />
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        padding: "15px",
                        gap: "10px",
                    }}
                >
                    <IonItem
                        onClick={() => {
                            dispatch(setShowOnBoardingFlow(false));
                            goToVia(
                                [
                                    routes.refsixFixtures,
                                    routes.refsixMoreIntegrations,
                                ],
                                routes.refsixAssignrIntegration
                            );
                        }}
                    >
                        <div
                            style={{
                                display: "flex",
                                margin: "15px",
                                alignItems: "center",
                            }}
                        >
                            <img
                                style={{ width: "64px" }}
                                src={assignrSquareLogo}
                            />
                            <IonText
                                style={{
                                    margin: "0 20px",
                                    fontSize: "20px",
                                }}
                            >
                                Assignr
                            </IonText>
                        </div>
                    </IonItem>
                    <IonItem
                        onClick={() => {
                            dispatch(setShowOnBoardingFlow(false));
                            goToVia(
                                [
                                    routes.refsixFixtures,
                                    routes.refsixMoreIntegrations,
                                ],
                                routes.refsixRefInsightIntegration
                            );
                        }}
                    >
                        <div
                            style={{
                                display: "flex",
                                margin: "15px",
                                alignItems: "center",
                            }}
                        >
                            <img
                                style={{ width: "64px" }}
                                src={refinsightSquareLogo}
                            />
                            <IonText
                                style={{
                                    margin: "0 20px",
                                    fontSize: "20px",
                                }}
                            >
                                Ref Insight
                            </IonText>
                        </div>
                    </IonItem>
                </div>
            </IonContent>
            <IonFooter>
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        marginTop: 20,
                    }}
                >
                    <IonButton
                        data-testid="button-go-maybe-later"
                        style={{ color: "white" }}
                        fill="clear"
                        onClick={() => {
                            dispatch(setShowOnBoardingFlow(false));
                            route.push(
                                routes.refsixFixtures,
                                "root",
                                "replace"
                            );
                        }}
                    >
                        {t("general.maybeLater")}
                    </IonButton>
                    <IonButton
                        data-testid="button-go-to-dbu-integration"
                        onClick={() => {
                            dispatch(setShowOnBoardingFlow(false));
                            // Add the route to the history stack
                            goToVia(
                                [routes.refsixFixtures],
                                routes.refsixMoreIntegrations
                            );
                        }}
                    >
                        {t("integrations.refInsight.link")}
                    </IonButton>
                </div>
            </IonFooter>
        </>
    );
};
export const OnBoardingSetupComplete: FC = () => {
    const { t } = useTranslation();
    const userProfile = useProfile();
    const isDanishUser = userProfile?.country?.toUpperCase() === "DK";
    const isUsUser = userProfile?.country?.toUpperCase() === "US";
    const route = useIonRouter();
    const dispatch = useDispatch();

    useEffect(() => {
        if (!isUsUser && !isDanishUser) {
            dispatch(setShowOnBoardingFlow(false));

            route.push(routes.refsixFixtures, "root", "replace");
        }
    }, [isUsUser, isDanishUser, route, dispatch]);

    return (
        <IonPage className="onboarding-complete-page">
            <HeaderComponent
                titleTestId="setup-complete-header"
                title={t("help.onBoarding.setupComplete")}
            />
            {isDanishUser ? (
                <DanishOnBoardingSetupComplete />
            ) : isUsUser ? (
                <UsOnBoardingSetupComplete />
            ) : null}
        </IonPage>
    );
};
